/**
 * Entrada do host ERP (`/`).
 *
 * - Sessão com permissão ERP → primeira tela liberada para o grupo
 * - Sem sessão → /login (nativo do ERP)
 * - Sessão sem permissão ERP → mensagem + sair (não redireciona para a loja)
 */

import { useMemo } from 'react'
import { Navigate, Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { usePermissions, MODULES, ACTIONS } from '../hooks/usePermissions'
import { getFirstAccessibleRoute } from '../utils/getFirstAccessibleRoute'

function ErpEntryRedirect() {
  const { isAuthenticated, isAdmin, logout, loading: authLoading } = useAuth()
  const { hasPermission, loading: permissionsLoading } = usePermissions()

  const waitingAuth = authLoading
  const waitingPermissions = isAuthenticated && permissionsLoading

  const hasErpAccess = useMemo(() => {
    if (!isAuthenticated) return false
    if (isAdmin()) return true
    return hasPermission(MODULES.ERP, ACTIONS.VIEW)
  }, [isAuthenticated, isAdmin, hasPermission])

  if (waitingAuth || waitingPermissions) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>Carregando...</div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  if (hasErpAccess) {
    const destination = getFirstAccessibleRoute(hasPermission, isAdmin(), false)
    if (destination) {
      return <Navigate to={destination} replace />
    }

    return (
      <div
        style={{
          minHeight: '60vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '1rem',
          padding: '2rem',
          textAlign: 'center',
        }}
      >
        <h1 style={{ margin: 0, fontSize: '1.35rem' }}>Nenhuma tela liberada</h1>
        <p style={{ margin: 0, maxWidth: '42ch', color: '#475569' }}>
          Sua conta acessa o ERP, mas o grupo não tem uma página inicial disponível.
          Peça ao administrador para revisar as autorizações.
        </p>
        <button type="button" onClick={() => logout()}>
          Sair
        </button>
      </div>
    )
  }

  return (
    <div
      style={{
        minHeight: '60vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '1rem',
        padding: '2rem',
        textAlign: 'center',
      }}
    >
      <h1 style={{ margin: 0, fontSize: '1.35rem' }}>Sem permissão para o ERP</h1>
      <p style={{ margin: 0, maxWidth: '36ch', color: '#475569' }}>
        Sua conta está autenticada, mas não tem acesso ao sistema. Peça ao
        administrador para incluir você em um grupo de permissões.
      </p>
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
        <button type="button" onClick={() => logout()}>
          Sair
        </button>
        <Link to="/login">Ir para o login</Link>
      </div>
    </div>
  )
}

export default ErpEntryRedirect
